// src/admin/components/services/ServiceManagement.jsx - Internationalized with RTL support
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  RiBuildingLine,
  RiArrowLeftLine,
  RiArrowRightLine,
  RiRefreshLine,
  RiMoneyDollarCircleLine,
  RiFilterLine,
  RiAddLine,
  RiFileChartLine,
  RiCalendarLine,
  RiUserLine,
  RiHome4Line,
  RiCheckLine,
  RiCloseCircleLine,
  RiAlertLine,
  RiInformationLine,
  RiUserStarLine,
  RiPencilLine,
  RiSearch2Line,
  RiArrowUpSLine,
  RiArrowDownSLine,
  RiCloseLine,
  RiPercentLine,
  RiFilterOffLine,
  RiSaveLine,
  RiPrinterLine
} from 'react-icons/ri';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useTheme } from '../../../contexts/ThemeContext';
import PrintServiceReceipt from './PrintServiceReceipt';
import {
  getBuildingFloors,
  getServicesByFloor,
  updateServicePayment,
  generateServicePayment,
  formatCurrency,
  getPaymentStatusColor,
  getMonthName
} from '../../../models/serviceModel';
import { getAllBuildings } from '../../../models/buildingModel';

const ServiceManagement = () => {
  const { t, i18n } = useTranslation();
  const { isDarkMode } = useTheme();
  const { buildingId } = useParams();
  const navigate = useNavigate();

  // Check if the current language is RTL
  const isRTL = i18n.language === 'ar' || i18n.language === 'ku';

  // Select the appropriate arrow icon based on RTL
  const BackArrow = isRTL ? RiArrowRightLine : RiArrowLeftLine;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [buildings, setBuildings] = useState([]);
  const [selectedBuilding, setSelectedBuilding] = useState(buildingId || '');
  const [floors, setFloors] = useState([]);
  const [floorData, setFloorData] = useState({});
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [expandedFloors, setExpandedFloors] = useState({});
  const [loadingFloorData, setLoadingFloorData] = useState({});
  const [updatingPayment, setUpdatingPayment] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Print Receipt State
  const [showReceipt, setShowReceipt] = useState(false);
  const [selectedPaymentForPrint, setSelectedPaymentForPrint] = useState(null);
  const [filteredFloors, setFilteredFloors] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all'); // 'all', 'paid', 'unpaid'
  const [submitting, setSubmitting] = useState(false);
  const [tooltipPayment, setTooltipPayment] = useState(null);
  // Selected month per zone (keyed by floor number), defaults to current month
  const [zoneSelectedMonth, setZoneSelectedMonth] = useState({});

  // Payment modal state
  const [paymentModal, setPaymentModal] = useState({
    isOpen: false,
    apartmentId: null,
    apartmentNumber: '',
    tenantName: '',
    ownerName: '',
    month: null,
    amount: '',
    isPaid: false,
    notes: ''
  });

  // Available years for filter (current year and 3 previous years)
  const availableYears = Array.from({ length: 4 }, (_, i) => selectedYear - i);

  // Load buildings
  useEffect(() => {
    const fetchBuildings = async () => {
      try {
        const response = await getAllBuildings();
        setBuildings(response.buildings || []);

        // If no building is selected but we have buildings, select the first one
        if (!selectedBuilding && response.buildings && response.buildings.length > 0) {
          setSelectedBuilding(response.buildings[0].id);
        }
      } catch (err) {
        console.error('Error loading buildings:', err);
        setError(t('failed_to_load_buildings') || 'Failed to load buildings. Please try again.');
      }
    };

    fetchBuildings();
  }, [t]);

  // Load floors when building is selected
  useEffect(() => {
    if (!selectedBuilding) return;

    const fetchFloors = async () => {
      try {
        setLoading(true);
        setError(null);
        setSuccess(null);

        const response = await getBuildingFloors(selectedBuilding, selectedYear);

        if (response.success) {
          setFloors(response.floors || []);
          setFilteredFloors(response.floors || []);

          // Expand first floor by default
          if (response.floors && response.floors.length > 0) {
            const firstFloor = response.floors[0].floor;
            setExpandedFloors({ [firstFloor]: true });

            // Load data for the first floor
            await fetchFloorData(selectedBuilding, firstFloor);
          }
        } else {
          throw new Error(response.message || t('failed_to_load_floors') || 'Failed to load floors');
        }
      } catch (err) {
        console.error('Error loading floors:', err);
        setError(err.message || t('failed_to_load_building_floors') || 'Failed to load building floors');
        setFloors([]); // Clear floors on error
        setFilteredFloors([]);
      } finally {
        setLoading(false);
      }
    };

    fetchFloors();
  }, [selectedBuilding, selectedYear, t]);

  // Apply search and status filters
  useEffect(() => {
    if (!searchTerm.trim() && statusFilter === 'all') {
      setFilteredFloors(floors);
      return;
    }

    const term = searchTerm.toLowerCase();

    // We'll need to check the floor data to filter by apartments/tenants
    const filterFloors = async () => {
      // Make sure we have data for all floors first
      const promises = [];
      for (const floor of floors) {
        if (!floorData[`${selectedBuilding}-${floor.floor}`]) {
          promises.push(fetchFloorData(selectedBuilding, floor.floor, false));
        }
      }

      if (promises.length > 0) {
        await Promise.all(promises);
      }

      // Now filter floors based on the search term and status filter
      const filteredFloors = floors.filter(floor => {
        // Check if floor data exists
        const floorApartments = floorData[`${selectedBuilding}-${floor.floor}`] || [];

        // Apply status filter at floor level if no search term
        if (!searchTerm.trim() && statusFilter !== 'all') {
          if (statusFilter === 'paid' && floor.paidCount === 0) return false;
          if (statusFilter === 'unpaid' && floor.unpaidCount === 0) return false;
        }

        // Check if floor number contains the term
        if (searchTerm.trim() && floor.floor.toString().includes(term)) {
          return true;
        }

        // Filter apartments based on search term and status
        const matchingApartments = floorApartments.filter(apt => {
          // Apply status filter
          if (statusFilter !== 'all') {
            const hasMatchingPayment = Object.values(apt.payments).some(payment => {
              return statusFilter === 'paid' ? payment.isPaid : !payment.isPaid;
            });

            if (!hasMatchingPayment) return false;
          }

          // Apply search term filter
          if (searchTerm.trim()) {
            return (
              (apt.apartmentNumber && apt.apartmentNumber.toLowerCase().includes(term)) ||
              (apt.tenantName && apt.tenantName.toLowerCase().includes(term)) ||
              (apt.ownerName && apt.ownerName.toLowerCase().includes(term))
            );
          }

          return true;
        });

        return matchingApartments.length > 0;
      });

      setFilteredFloors(filteredFloors);

      // Auto-expand floors with matches
      const expandedMap = {};
      filteredFloors.forEach(floor => {
        expandedMap[floor.floor] = true;
      });
      setExpandedFloors(expandedMap);
    };

    filterFloors();
  }, [searchTerm, statusFilter, floors, floorData, selectedBuilding]);

  // Fetch floor data (apartments and their service payments)
  const fetchFloorData = async (buildingId, floor, setLoading = true) => {
    try {
      if (setLoading) {
        setLoadingFloorData(prev => ({
          ...prev,
          [floor]: true
        }));
      }

      const response = await getServicesByFloor(buildingId, floor, selectedYear);

      if (response.success) {
        setFloorData(prev => ({
          ...prev,
          [`${buildingId}-${floor}`]: response.apartments || []
        }));
        return response.apartments || [];
      } else {
        throw new Error(response.message || t('failed_to_load_floor_data') || 'Failed to load floor data');
      }
    } catch (err) {
      console.error(`Error loading floor ${floor} data:`, err);
      if (setLoading) {
        setError(err.message || t('failed_to_load_floor_data') || 'Failed to load floor data');
      }
      return [];
    } finally {
      if (setLoading) {
        setLoadingFloorData(prev => ({
          ...prev,
          [floor]: false
        }));
      }
    }
  };

  // Toggle floor expansion
  const toggleFloorExpansion = async (floor) => {
    const isCurrentlyExpanded = expandedFloors[floor] || false;

    setExpandedFloors(prev => ({
      ...prev,
      [floor]: !isCurrentlyExpanded
    }));

    // Fetch floor data if expanding and not already loaded
    if (!isCurrentlyExpanded && selectedBuilding && !floorData[`${selectedBuilding}-${floor}`]) {
      await fetchFloorData(selectedBuilding, floor);
    }
  };

  // Handle building change
  const handleBuildingChange = (e) => {
    setSelectedBuilding(e.target.value);
    setFloorData({});  // Clear floor data when building changes
    setExpandedFloors({});  // Reset expanded floors
    setSearchTerm(''); // Clear search term
    setStatusFilter('all'); // Reset status filter
  };

  // Handle year change
  const handleYearChange = (e) => {
    setSelectedYear(parseInt(e.target.value));
    setFloorData({});  // Clear floor data when year changes
    setExpandedFloors({});  // Reset expanded floors
  };

  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  // Handle status filter change
  const handleStatusFilterChange = (status) => {
    setStatusFilter(status);
  };

  // Clear filters
  const clearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
  };

  // Refresh data
  const handleRefresh = async () => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      const response = await getBuildingFloors(selectedBuilding, selectedYear);

      if (response.success) {
        setFloors(response.floors || []);
        setFilteredFloors(response.floors || []);

        // Reload data for expanded floors
        const promises = [];
        for (const floor in expandedFloors) {
          if (expandedFloors[floor]) {
            promises.push(fetchFloorData(selectedBuilding, floor));
          }
        }

        if (promises.length > 0) {
          await Promise.all(promises);
        }

        setSuccess(t('data_refreshed_successfully') || 'Data refreshed successfully.');

        // Auto-hide success message after 3 seconds
        setTimeout(() => {
          setSuccess(null);
        }, 3000);
      } else {
        throw new Error(response.message || t('failed_to_refresh_data') || 'Failed to refresh data');
      }
    } catch (err) {
      console.error('Error refreshing data:', err);
      setError(err.message || t('failed_to_refresh_data') || 'Failed to refresh data');
    } finally {
      setLoading(false);
    }
  };

  // Update the handleGeneratePayment function
  const handleGeneratePayment = (apartmentId, month) => {
    // Find the apartment details
    const floorKey = Object.keys(floorData).find(key => {
      return floorData[key].some(apt => apt.apartmentId === apartmentId);
    });

    const apartment = floorData[floorKey]?.find(apt => apt.apartmentId === apartmentId);

    if (!apartment) return;

    // Open the payment modal with pre-filled information
    setPaymentModal({
      isOpen: true,
      apartmentId,
      apartmentNumber: apartment.apartmentNumber,
      tenantName: apartment.tenantName,
      ownerName: apartment.ownerName,
      month,
      amount: '',
      isPaid: false,
      notes: `Service payment for ${getMonthName(month)} ${selectedYear}`
    });
  };

  // Add a new function to handle the payment submission
  const handlePaymentSubmit = async () => {
    try {
      // Validate the amount
      const paymentAmount = parseFloat(paymentModal.amount);
      if (isNaN(paymentAmount) || paymentAmount <= 0) {
        setError(t('invalid_amount') || 'Please enter a valid amount greater than 0');
        return;
      }

      setError(null);
      setSubmitting(true);

      // Call the API to generate the payment
      const response = await generateServicePayment({
        apartmentId: paymentModal.apartmentId,
        month: paymentModal.month,
        year: selectedYear,
        amount: paymentAmount,
        status: paymentModal.isPaid ? 'paid' : 'unpaid',
        notes: paymentModal.notes,
        recipientType: 'apartment' // Ensure recipientType is set
      });

      if (response.success) {
        // Always print receipt after generating a payment
        if (response.generatedIds && response.generatedIds.length > 0) {
          const newPayment = {
            id: response.generatedIds[0],
            apartmentId: paymentModal.apartmentId,
            apartmentNumber: paymentModal.apartmentNumber,
            tenantName: paymentModal.tenantName,
            ownerName: paymentModal.ownerName,
            month: paymentModal.month,
            year: selectedYear,
            amount: paymentAmount,
            isPaid: paymentModal.isPaid,
            paymentDate: new Date().toISOString(),
            buildingName: buildings.find(b => b.id === selectedBuilding)?.name || ''
          };
          setSelectedPaymentForPrint({ ...newPayment, autoPrint: true });
          setShowReceipt(true);
        }

        // Close the modal
        setPaymentModal(prev => ({ ...prev, isOpen: false }));

        setSuccess(t('payment_generated_successfully') || 'Payment generated successfully');

        // Refresh the data
        await handleRefresh();

        // Auto-hide success message after 3 seconds
        setTimeout(() => {
          setSuccess(null);
        }, 3000);
      } else {
        throw new Error(response.message || t('failed_to_generate_payment'));
      }
    } catch (err) {
      console.error('Error generating payment:', err);
      setError(err.message || t('failed_to_generate_payment'));
    } finally {
      setSubmitting(false);
    }
  };

  // Mark payment as paid/unpaid
  const handleTogglePayment = async (paymentId, isPaid) => {
    try {
      setUpdatingPayment(paymentId);

      const updateData = {
        isPaid: !isPaid,
        paymentDate: !isPaid ? new Date().toISOString().split('T')[0] : null,
        notes: !isPaid
          ? t('marked_as_paid_via_service_management') || 'Marked as paid via service management'
          : t('marked_as_unpaid_via_service_management') || 'Marked as unpaid via service management'
      };

      const response = await updateServicePayment(paymentId, updateData);

      if (response.success) {
        setSuccess(
          !isPaid
            ? t('payment_marked_as_paid') || 'Payment marked as paid successfully.'
            : t('payment_marked_as_unpaid') || 'Payment marked as unpaid successfully.'
        );

        // Auto-hide success message after 3 seconds
        setTimeout(() => {
          setSuccess(null);
        }, 3000);
      }

      // After successful update, refresh the data
      await handleRefresh();
    } catch (err) {
      console.error('Error updating payment:', err);
      setError(err.message || t('failed_to_update_payment') || 'Failed to update payment');
    } finally {
      setUpdatingPayment(null);
    }
  };

  // Go to payment details
  const viewPaymentDetails = (paymentId) => {
    navigate(`/admin/services/${paymentId}`);
  };

  // Show payment tooltip
  const showTooltip = (payment) => {
    setTooltipPayment(payment);
  };

  // Hide payment tooltip
  const hideTooltip = () => {
    setTooltipPayment(null);
  };

  // Month columns
  const monthColumns = Array.from({ length: 12 }, (_, i) => i + 1);

  // Payment tooltip component
  const PaymentTooltip = ({ payment }) => {
    if (!payment) return null;

    return (
      <div className={`absolute z-20 bottom-full mb-2 ${isRTL ? 'right-1/2 transform translate-x-1/2' : 'left-1/2 transform -translate-x-1/2'} w-48 p-2 rounded-lg shadow-lg ${isDarkMode ? 'bg-zinc-800 border border-gray-600' : 'bg-white border border-gray-200'
        }`}>
        <div className="text-xs">
          <div className={`font-medium mb-1 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            {payment.isPaid ? t('payment_details') || 'Payment Details' : t('payment_due') || 'Payment Due'}
          </div>

          <div className="flex justify-between mb-1">
            <span className={isDarkMode ? 'text-zinc-400' : 'text-gray-500'}>{t('amount') || 'Amount'}:</span>
            <span className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              {formatCurrency(payment.amount)}
            </span>
          </div>

          <div className="flex justify-between mb-1">
            <span className={isDarkMode ? 'text-zinc-400' : 'text-gray-500'}>{t('status') || 'Status'}:</span>
            <span className={`font-medium ${payment.isPaid
              ? (isDarkMode ? 'text-green-400' : 'text-green-600')
              : (isDarkMode ? 'text-red-400' : 'text-red-600')
              }`}>
              {payment.isPaid ? t('paid') || 'Paid' : t('unpaid') || 'Unpaid'}
            </span>
          </div>

          {payment.isPaid && payment.paymentDate && (
            <div className="flex justify-between">
              <span className={isDarkMode ? 'text-zinc-400' : 'text-gray-500'}>{t('paid_on') || 'Paid on'}:</span>
              <span className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                {new Date(payment.paymentDate).toLocaleDateString(i18n.language)}
              </span>
            </div>
          )}

          {/* Triangle pointer */}
          <div className={`absolute ${isRTL ? 'right-1/2 transform translate-x-1/2' : 'left-1/2 transform -translate-x-1/2'} bottom-0 translate-y-full w-0 h-0 border-l-8 border-r-8 border-t-8 border-l-transparent border-r-transparent ${isDarkMode ? 'border-t-gray-700' : 'border-t-white'}`}></div>
        </div>
      </div>
    );
  };

  // Payment Modal Component
  const PaymentModal = () => {
    if (!paymentModal.isOpen) return null;

    const monthName = getMonthName(paymentModal.month);
    const title = t('generate_payment_for', {
      month: monthName,
      year: selectedYear,
      apartment: paymentModal.apartmentNumber
    }) || `Generate Payment for ${monthName} ${selectedYear} - ${paymentModal.apartmentNumber}`;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
        <div className={`max-w-md w-full rounded-xl overflow-hidden shadow-2xl transform transition-all duration-300 ${isDarkMode ? 'bg-zinc-900 border border-gray-700' : 'bg-white'
          }`}>
          {/* Header */}
          <div className={`p-4 ${isDarkMode ? 'bg-gray-750 border-b border-gray-700' : 'bg-purple-50 border-b border-gray-200'}`}>
            <div className="flex items-center">
              <RiMoneyDollarCircleLine className={`${isRTL ? 'ml-3' : 'mr-3'} ${isDarkMode ? 'text-purple-400' : 'text-purple-500'}`} size={24} />
              <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                {title}
              </h3>
            </div>
          </div>

          {/* Body */}
          <div className="p-6 space-y-4">
            {/* Apartment & Resident Info */}
            <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-gray-750' : 'bg-gray-50'}`}>
              <div className="flex items-center">
                <RiHome4Line className={`${isRTL ? 'ml-2' : 'mr-2'} ${isDarkMode ? 'text-red-400' : 'text-red-500'}`} size={18} />
                <span className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-700'}`}>
                  {paymentModal.apartmentNumber}
                </span>
              </div>

              {(paymentModal.tenantName || paymentModal.ownerName) && (
                <div className="flex items-center mt-2">
                  {paymentModal.tenantName ? (
                    <>
                      <RiUserLine className={`${isRTL ? 'ml-2' : 'mr-2'} ${isDarkMode ? 'text-purple-400' : 'text-purple-500'}`} size={16} />
                      <span className={isDarkMode ? 'text-gray-300' : 'text-gray-600'}>
                        {paymentModal.tenantName}
                      </span>
                    </>
                  ) : paymentModal.ownerName ? (
                    <>
                      <RiUserStarLine className={`${isRTL ? 'ml-2' : 'mr-2'} ${isDarkMode ? 'text-red-400' : 'text-red-500'}`} size={16} />
                      <span className={isDarkMode ? 'text-gray-300' : 'text-gray-600'}>
                        {paymentModal.ownerName}
                      </span>
                    </>
                  ) : null}
                </div>
              )}
            </div>

            {/* Amount Input */}
            <div>
              <label className={`block mb-1 text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                {t('payment_amount') || 'Payment Amount'} *
              </label>
              <div className="relative">
                <div className={`absolute inset-y-0 start-0 ps-3 flex items-center pointer-events-none ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'
                  }`}>
                  <RiMoneyDollarCircleLine size={18} />
                </div>
                <input
                  type="number"
                  value={paymentModal.amount}
                  onChange={(e) => setPaymentModal(prev => ({ ...prev, amount: e.target.value }))}
                  className={`w-full p-2.5 ps-10 rounded-lg border ${isDarkMode ? 'bg-zinc-800 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
                    } focus:ring-2 focus:ring-red-500`}
                  placeholder={t('enter_amount') || 'Enter amount'}
                  min="0.01"
                  step="0.01"
                  required
                  autoFocus
                />
              </div>
            </div>

            {/* Payment Status */}
            <div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="isPaidCheckbox"
                  checked={paymentModal.isPaid}
                  onChange={(e) => setPaymentModal(prev => ({ ...prev, isPaid: e.target.checked }))}
                  className={`w-4 h-4 rounded ${isDarkMode ? 'bg-zinc-800 border-gray-600' : 'bg-white border-gray-300'
                    } text-red-600 focus:ring-red-500`}
                />
                <label
                  htmlFor="isPaidCheckbox"
                  className={`ml-2 text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}
                >
                  {t('mark_as_paid_immediately') || 'Mark as paid immediately'}
                </label>
              </div>
            </div>

            {/* Notes */}
            <div>
              <label className={`block mb-1 text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                {t('notes') || 'Notes'}
              </label>
              <textarea
                value={paymentModal.notes}
                onChange={(e) => setPaymentModal(prev => ({ ...prev, notes: e.target.value }))}
                className={`w-full p-2.5 rounded-lg border ${isDarkMode ? 'bg-zinc-800 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
                  } focus:ring-2 focus:ring-red-500`}
                rows={2}
                placeholder={t('payment_notes') || 'Add notes about this payment'}
              />
            </div>

            <div className={`text-xs ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`}>
              * {t('required_fields') || 'Required fields'}
            </div>
          </div>

          {/* Footer with Actions */}
          <div className={`p-4 ${isDarkMode ? 'border-t border-gray-700' : 'border-t border-gray-200'}`}>
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => setPaymentModal(prev => ({ ...prev, isOpen: false }))}
                className={`px-4 py-2 rounded-lg ${isDarkMode ? 'bg-zinc-800 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  } transition-colors`}
              >
                {t('cancel') || 'Cancel'}
              </button>

              <button
                type="button"
                onClick={handlePaymentSubmit}
                className={`px-4 py-2 rounded-lg ${paymentModal.amount && !isNaN(parseFloat(paymentModal.amount)) && parseFloat(paymentModal.amount) > 0 && !submitting
                  ? (isDarkMode ? 'bg-red-600 text-white hover:bg-red-700' : 'bg-red-600 text-white hover:bg-red-700')
                  : (isDarkMode ? 'bg-gray-600 text-zinc-400 cursor-not-allowed' : 'bg-gray-300 text-gray-500 cursor-not-allowed')
                  } transition-colors shadow-md flex items-center`}
                disabled={!paymentModal.amount || isNaN(parseFloat(paymentModal.amount)) || parseFloat(paymentModal.amount) <= 0 || submitting}
              >
                {submitting ? (
                  <div className={`h-5 w-5 border-2 border-white border-t-transparent rounded-full animate-spin ${isRTL ? 'ml-2' : 'mr-2'}`}></div>
                ) : (
                  <RiSaveLine className={`${isRTL ? 'ml-1.5' : 'mr-1.5'}`} size={18} />
                )}
                {submitting ? t('processing') || 'Processing...' : t('generate_payment') || 'Generate Payment'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div dir={isRTL ? 'rtl' : 'ltr'} className="space-y-6">
      {/* Add the PaymentModal component */}
      <PaymentModal />

      {/* Print Modal */}
      {showReceipt && selectedPaymentForPrint && (
        <PrintServiceReceipt
          payment={selectedPaymentForPrint}
          onClose={() => setShowReceipt(false)}
          autoPrint={selectedPaymentForPrint.autoPrint ?? true}
        />
      )}

      {/* Header */}
      <div className={`${isDarkMode ? 'bg-zinc-900 border-gray-700' : 'bg-white border-gray-200'} rounded-xl border shadow-lg overflow-hidden`}>
        <div className={`p-6 ${isDarkMode ? 'bg-gray-750 border-b border-gray-700' : 'bg-purple-50 border-b border-gray-200'}`}>
          <div className="flex justify-between items-center">
            <div className="flex items-center">
              <RiMoneyDollarCircleLine className={`text-2xl ${isRTL ? 'ml-3' : 'mr-3'} ${isDarkMode ? 'text-purple-400' : 'text-purple-500'}`} />
              <h1 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                {t('service_payments_management') || 'Service Payments Management'}
              </h1>
            </div>

            <div className={`flex items-center ${isRTL ? 'space-x-2 space-x-reverse' : 'space-x-2'}`}>
              <button
                onClick={handleRefresh}
                className={`p-2 rounded-full ${isDarkMode ? 'bg-zinc-800 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                title={t('refresh') || 'Refresh'}
              >
                <RiRefreshLine size={20} />
              </button>

              <Link
                to="/admin/dashboard"
                className={`p-2 rounded-full ${isDarkMode ? 'bg-zinc-800 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                title={t('back_to_dashboard') || 'Back to Dashboard'}
              >
                <BackArrow size={20} />
              </Link>
            </div>
          </div>
        </div>

        {/* Controls */}
        <div className="p-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex flex-col sm:flex-row gap-4">
              {/* Building Selector */}
              <div className="w-full sm:w-64">
                <label className={`block mb-1 text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  {t('building') || 'Building'}
                </label>
                <select
                  value={selectedBuilding}
                  onChange={handleBuildingChange}
                  className={`w-full p-2.5 rounded-lg border ${isDarkMode ? 'bg-zinc-800 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
                    }`}
                >
                  <option value="">{t('select_building') || 'Select Building'}</option>
                  {buildings.map(building => (
                    <option key={building.id} value={building.id}>
                      {building.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Year Selector */}
              <div className="w-full sm:w-auto">
                <label className={`block mb-1 text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  {t('year') || 'Year'}
                </label>
                <select
                  value={selectedYear}
                  onChange={handleYearChange}
                  className={`w-full p-2.5 rounded-lg border ${isDarkMode ? 'bg-zinc-800 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
                    }`}
                >
                  {availableYears.map(year => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
              </div>

              {/* Search Box */}
              <div className="w-full sm:w-64">
                <label className={`block mb-1 text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  {t('search') || 'Search'}
                </label>
                <div className="relative">
                  <div className={`absolute inset-y-0 ${isRTL ? 'right-0 pr-3' : 'left-0 pl-3'} flex items-center pointer-events-none`}>
                    <RiSearch2Line className={isDarkMode ? 'text-zinc-400' : 'text-gray-500'} />
                  </div>
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={handleSearchChange}
                    className={`w-full ${isRTL ? 'pr-10 pl-10' : 'pl-10 pr-10'} p-2.5 rounded-lg border ${isDarkMode ? 'bg-zinc-800 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
                      }`}
                    placeholder={t('search_apartments_tenants') || 'Search apartments, tenants...'}
                  />
                  {searchTerm && (
                    <button
                      onClick={() => setSearchTerm('')}
                      className={`absolute inset-y-0 ${isRTL ? 'left-0 pl-3' : 'right-0 pr-3'} flex items-center`}
                    >
                      <RiCloseCircleLine className={isDarkMode ? 'text-zinc-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'} />
                    </button>
                  )}
                </div>
              </div>
            </div>

            <div className={`flex flex-wrap gap-2 ${isRTL ? 'space-x-reverse' : ''}`}>
              <Link
                to={`/admin/services/generate`}
                className={`px-4 py-2 rounded-lg flex items-center ${isDarkMode ? 'bg-red-600 text-white hover:bg-red-700' : 'bg-red-600 text-white hover:bg-red-700'
                  }`}
              >
                <RiAddLine className={`${isRTL ? 'ml-2' : 'mr-2'}`} size={18} />
                {t('generate_payments') || 'Generate Payments'}
              </Link>

              <Link
                to={`/admin/services/stats?buildingId=${selectedBuilding}&year=${selectedYear}`}
                className={`px-4 py-2 rounded-lg flex items-center ${isDarkMode ? 'bg-zinc-700 text-white hover:bg-zinc-600' : 'bg-zinc-200 text-gray-800 hover:bg-zinc-300'
                  }`}
              >
                <RiFileChartLine className={`${isRTL ? 'ml-2' : 'mr-2'}`} size={18} />
                {t('view_statistics') || 'View Statistics'}
              </Link>
            </div>
          </div>

          {/* Status Filter Buttons */}
          <div className={`flex flex-wrap gap-2 mt-4 ${isRTL ? 'space-x-reverse' : ''}`}>
            <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              {t('filter_by_status') || 'Filter by status'}:
            </span>
            <button
              onClick={() => handleStatusFilterChange('all')}
              className={`px-3 py-1 text-xs rounded-lg ${statusFilter === 'all'
                ? (isDarkMode ? 'bg-zinc-600 text-white' : 'bg-zinc-600 text-white')
                : (isDarkMode ? 'bg-zinc-800 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200')
                }`}
            >
              {t('all') || 'All'}
            </button>
            <button
              onClick={() => handleStatusFilterChange('paid')}
              className={`px-3 py-1 text-xs rounded-lg flex items-center ${statusFilter === 'paid'
                ? (isDarkMode ? 'bg-green-600 text-white' : 'bg-green-500 text-white')
                : (isDarkMode ? 'bg-zinc-800 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200')
                }`}
            >
              <RiCheckLine className={`${isRTL ? 'ml-1' : 'mr-1'}`} size={14} />
              {t('paid') || 'Paid'}
            </button>
            <button
              onClick={() => handleStatusFilterChange('unpaid')}
              className={`px-3 py-1 text-xs rounded-lg flex items-center ${statusFilter === 'unpaid'
                ? (isDarkMode ? 'bg-red-600 text-white' : 'bg-zinc-500 text-white')
                : (isDarkMode ? 'bg-zinc-800 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200')
                }`}
            >
              <RiCloseCircleLine className={`${isRTL ? 'ml-1' : 'mr-1'}`} size={14} />
              {t('unpaid') || 'Unpaid'}
            </button>

            {(searchTerm || statusFilter !== 'all') && (
              <button
                onClick={clearFilters}
                className={`px-3 py-1 text-xs rounded-lg flex items-center ${isDarkMode ? 'bg-zinc-800 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
              >
                <RiFilterOffLine className={`${isRTL ? 'ml-1' : 'mr-1'}`} size={14} />
                {t('clear_filters') || 'Clear Filters'}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Success Message */}
      {success && (
        <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-green-900 bg-opacity-20 border-green-800 text-green-400' : 'bg-green-50 border-green-200 text-green-700'
          } border flex items-center`}>
          <RiCheckLine className={`${isRTL ? 'ml-2' : 'mr-2'} text-xl`} />
          {success}
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-red-900 bg-opacity-20 border-red-800 text-red-400' : 'bg-zinc-50 border-red-200 text-red-700'
          } border flex items-center`}>
          <RiAlertLine className={`${isRTL ? 'ml-2' : 'mr-2'} text-xl`} />
          {error}
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className={`${isDarkMode ? 'bg-zinc-900 border-gray-700' : 'bg-white border-gray-200'} rounded-xl border shadow-lg p-8 text-center`}>
          <div className="flex flex-col items-center justify-center space-y-4">
            <div className={`inline-block h-12 w-12 animate-spin rounded-full border-4 ${isDarkMode ? 'border-red-500 border-t-transparent' : 'border-red-500 border-t-transparent'
              }`}></div>
            <p className={`mt-2 text-lg font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              {t('loading_service_data') || 'Loading service payment data...'}
            </p>
          </div>
        </div>
      )}

      {/* No Building Selected */}
      {!selectedBuilding && !loading && (
        <div className={`${isDarkMode ? 'bg-zinc-900 border-gray-700' : 'bg-white border-gray-200'} rounded-xl border shadow-lg p-8 text-center`}>
          <RiBuildingLine className={`mx-auto text-6xl mb-4 ${isDarkMode ? 'text-gray-600' : 'text-zinc-400'}`} />
          <h3 className={`text-lg font-semibold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            {t('no_building_selected') || 'No Building Selected'}
          </h3>
          <p className={`mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            {t('please_select_building') || 'Please select a building to view service payments.'}
          </p>
        </div>
      )}

      {/* Search results */}
      {selectedBuilding && !loading && filteredFloors.length === 0 && (
        <div className={`${isDarkMode ? 'bg-zinc-900 border-gray-700' : 'bg-white border-gray-200'} rounded-xl border shadow-lg p-8 text-center`}>
          <RiSearch2Line className={`mx-auto text-6xl mb-4 ${isDarkMode ? 'text-gray-600' : 'text-zinc-400'}`} />
          <h3 className={`text-lg font-semibold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            {t('no_search_results') || 'No Search Results'}
          </h3>
          <p className={`mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            {t('no_matching_apartments') || 'No apartments or tenants match your search criteria.'}
          </p>
          <button
            onClick={clearFilters}
            className={`px-4 py-2 rounded-lg ${isDarkMode ? 'bg-zinc-700 text-white hover:bg-zinc-600' : 'bg-zinc-600 text-white hover:bg-zinc-500'
              }`}
          >
            {t('clear_filters') || 'Clear Filters'}
          </button>
        </div>
      )}

      {/* Floor Sections */}
      {selectedBuilding && !loading && filteredFloors.length > 0 && (
        <div className="space-y-6">
          {/* Title with the building name */}
          <div className={`${isDarkMode ? 'bg-zinc-900 border-gray-700' : 'bg-white border-gray-200'} rounded-xl border shadow-lg p-6`}>
            <h2 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'} text-center`}>
              {t('service_payments_for_building_year', {
                building: buildings.find(b => b.id == selectedBuilding)?.name || t('building') || 'Building',
                year: selectedYear
              }) || `Service Payments for ${buildings.find(b => b.id == selectedBuilding)?.name || 'Building'} - ${selectedYear}`}
            </h2>
          </div>

          {/* Each Floor */}
          {filteredFloors.map(floor => {
            const isExpanded = expandedFloors[floor.floor] || false;
            const floorKey = `${selectedBuilding}-${floor.floor}`;
            const apartments = floorData[floorKey] || [];
            const isLoadingFloor = loadingFloorData[floor.floor] || false;

            return (
              <div key={floor.floor} className={`${isDarkMode ? 'bg-zinc-900 border-gray-700' : 'bg-white border-gray-200'} rounded-xl border shadow-lg overflow-hidden`}>
                {/* Floor Header */}
                <div
                  onClick={() => toggleFloorExpansion(floor.floor)}
                  className={`p-4 ${isDarkMode ? 'bg-gray-750 hover:bg-zinc-800' : 'bg-purple-50 hover:bg-purple-100'} cursor-pointer transition-colors`}
                >
                  <div className="flex justify-between items-center">
                    <div className="flex items-center">
                      <RiHome4Line className={`text-xl ${isRTL ? 'ml-3' : 'mr-3'} ${isDarkMode ? 'text-purple-400' : 'text-purple-500'}`} />
                      <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        {t('zone_display', { number: floor.floor }) || `Zone ${floor.floor}`}
                      </h3>
                      <div className={`${isRTL ? 'mr-4' : 'ml-4'} text-sm flex items-center ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                        <RiHome4Line className={`${isRTL ? 'ml-1' : 'mr-1'}`} size={14} />
                        {floor.totalApartments} {t('showroom_units', 'Showroom Units')}
                        <span className="mx-2">•</span>
                        <RiCalendarLine className={`${isRTL ? 'ml-1' : 'mr-1'}`} size={14} />
                        {selectedYear}
                      </div>
                    </div>

                    <div className={`flex items-center ${isRTL ? 'space-x-4 space-x-reverse' : 'space-x-4'}`}>
                      <div className={`px-3 py-1 rounded-full text-sm flex items-center ${isDarkMode ? 'bg-green-900 bg-opacity-20 text-green-400' : 'bg-green-100 text-green-800'
                        }`}>
                        <RiCheckLine className={`${isRTL ? 'ml-1' : 'mr-1'}`} size={14} />
                        {floor.paidCount} {t('paid') || 'Paid'}
                      </div>
                      <div className={`px-3 py-1 rounded-full text-sm flex items-center ${isDarkMode ? 'bg-red-900 bg-opacity-20 text-red-400' : 'bg-zinc-100 text-red-800'
                        }`}>
                        <RiCloseCircleLine className={`${isRTL ? 'ml-1' : 'mr-1'}`} size={14} />
                        {floor.unpaidCount} {t('unpaid') || 'Unpaid'}
                      </div>
                      <div className={`px-3 py-1 rounded-full text-sm flex items-center ${isDarkMode ? 'bg-red-900 bg-opacity-20 text-red-400' : 'bg-zinc-100 text-red-800'
                        }`}>
                        <RiPercentLine className={`${isRTL ? 'ml-1' : 'mr-1'}`} size={14} />
                        {floor.paymentRate}% {t('rate') || 'Rate'}
                      </div>
                      {/* Expand/Collapse indicator */}
                      <button className="focus:outline-none">
                        {expandedFloors[floor.floor] ? (
                          <RiArrowUpSLine size={20} className={isDarkMode ? 'text-zinc-400' : 'text-gray-500'} />
                        ) : (
                          <RiArrowDownSLine size={20} className={isDarkMode ? 'text-zinc-400' : 'text-gray-500'} />
                        )}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Expanded Floor Content — Month-tab + Map Grid */}
                {isExpanded && (() => {
                  const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
                  const currentMonth = new Date().getMonth() + 1;
                  const activeMonth = zoneSelectedMonth[floor.floor] ?? currentMonth;

                  // Sort and group by rowPosition
                  const sorted = [...apartments].sort((a, b) => {
                    const na = parseInt(a.apartmentNumber); const nb = parseInt(b.apartmentNumber);
                    return isNaN(na) || isNaN(nb) ? a.apartmentNumber.localeCompare(b.apartmentNumber) : na - nb;
                  });
                  const hasPosData = sorted.some(a => a.rowPosition);
                  let frontRow, backRow, cornerUnits;
                  if (hasPosData) {
                    frontRow    = sorted.filter(a => a.rowPosition === 'front');
                    backRow     = [...sorted.filter(a => a.rowPosition === 'back')].reverse();
                    cornerUnits = sorted.filter(a => a.rowPosition === 'corner');
                  } else {
                    const half = Math.floor(sorted.length / 2);
                    frontRow    = sorted.slice(0, half);
                    backRow     = [...sorted.slice(half)].reverse();
                    cornerUnits = [];
                  }

                  // Unit card component
                  const UnitCard = ({ apt }) => {
                    const payment = apt.payments?.[activeMonth];
                    const personName = apt.tenantName || apt.ownerName;
                    const isPaid = payment?.isPaid;
                    const hasPayment = !!payment;

                    let cardBorder, badgeBg, badgeText, badgeLabel;
                    if (hasPayment && isPaid) {
                      cardBorder = 'border-green-500/40';
                      badgeBg = isDarkMode ? 'bg-green-500/15' : 'bg-green-50';
                      badgeText = isDarkMode ? 'text-green-400' : 'text-green-700';
                      badgeLabel = `✓ ${t('paid')}`;
                    } else if (hasPayment && !isPaid) {
                      cardBorder = 'border-red-500/40';
                      badgeBg = isDarkMode ? 'bg-red-500/15' : 'bg-red-50';
                      badgeText = isDarkMode ? 'text-red-400' : 'text-red-600';
                      badgeLabel = `✗ ${t('unpaid')}`;
                    } else {
                      cardBorder = isDarkMode ? 'border-zinc-700' : 'border-zinc-200';
                      badgeBg = isDarkMode ? 'bg-zinc-800' : 'bg-zinc-100';
                      badgeText = isDarkMode ? 'text-zinc-500' : 'text-zinc-400';
                      badgeLabel = `— ${t('none')}`;
                    }

                    return (
                      <div className={`rounded-xl border flex flex-col gap-2 p-3 shrink-0 w-[130px]
                        ${isDarkMode ? 'bg-zinc-800/80' : 'bg-white'} ${cardBorder}
                        transition-all hover:shadow-lg hover:-translate-y-0.5`}>

                        {/* Unit number */}
                        <div className="flex items-center justify-between">
                          <span className={`font-mono font-black text-2xl leading-none ${isDarkMode ? 'text-white' : 'text-zinc-900'}`}>
                            {apt.apartmentNumber}
                          </span>
                          {personName ? (
                            <span className={`text-[10px] font-medium truncate max-w-[60px] ${isDarkMode ? 'text-zinc-400' : 'text-zinc-500'} text-right`}>
                              {personName.split(' ')[0]}
                            </span>
                          ) : (
                            <span className={`text-[10px] italic ${isDarkMode ? 'text-zinc-600' : 'text-zinc-400'}`}>{t('vacant')}</span>
                          )}
                        </div>

                        {/* Payment badge */}
                        <div className={`flex items-center justify-between px-2 py-1.5 rounded-lg ${badgeBg}`}>
                          <span className={`text-[11px] font-bold ${badgeText}`}>{badgeLabel}</span>
                          {payment && (
                            <span className={`text-[11px] font-semibold ${badgeText}`}>
                              {formatCurrency(payment.amount)}
                            </span>
                          )}
                        </div>

                        {/* Action buttons */}
                        <div className="flex gap-1.5">
                          {hasPayment ? (
                            <>
                              <button
                                onClick={() => handleTogglePayment(payment.id, isPaid)}
                                disabled={updatingPayment === payment.id}
                                title={isPaid ? t('mark_as_unpaid') : t('mark_as_paid')}
                                className={`flex-1 py-1 rounded-lg text-[10px] font-bold transition-colors
                                  ${isPaid
                                    ? (isDarkMode ? 'bg-red-900/30 text-red-400 hover:bg-red-900/50' : 'bg-red-100 text-red-600 hover:bg-red-200')
                                    : (isDarkMode ? 'bg-green-900/30 text-green-400 hover:bg-green-900/50' : 'bg-green-100 text-green-600 hover:bg-green-200')
                                  } ${updatingPayment === payment.id ? 'opacity-50 cursor-not-allowed' : ''}`}
                              >
                                {updatingPayment === payment.id ? '...' : isPaid ? `✗ ${t('mark_as_unpaid')}` : `✓ ${t('mark_as_paid')}`}
                              </button>
                              <button
                                onClick={() => viewPaymentDetails(payment.id)}
                                title="View Details"
                                className={`px-2 py-1 rounded-lg text-[10px] font-bold transition-colors
                                  ${isDarkMode ? 'bg-zinc-700 text-zinc-300 hover:bg-zinc-600' : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200'}`}
                              >
                                ↗
                              </button>
                              {isPaid && (
                                <button
                                  onClick={() => {
                                    setSelectedPaymentForPrint({
                                      id: payment.id,
                                      apartmentId: apt.apartmentId,
                                      apartmentNumber: apt.apartmentNumber,
                                      tenantName: apt.tenantName,
                                      ownerName: apt.ownerName,
                                      month: activeMonth,
                                      year: selectedYear,
                                      amount: payment.amount,
                                      isPaid: true,
                                      paymentDate: payment.paymentDate || new Date().toISOString(),
                                      buildingName: buildings.find(b => b.id === selectedBuilding)?.name || ''
                                    });
                                    setShowReceipt(true);
                                  }}
                                  title="Print Receipt"
                                  className={`px-2 py-1 rounded-lg text-[10px] font-bold transition-colors
                                    ${isDarkMode ? 'bg-teal-900/40 text-teal-400 hover:bg-teal-900/60' : 'bg-teal-50 text-teal-600 hover:bg-teal-100'}`}
                                >
                                  <RiPrinterLine size={12} />
                                </button>
                              )}
                            </>
                          ) : (
                            <button
                              onClick={() => handleGeneratePayment(apt.apartmentId, activeMonth)}
                              className={`flex-1 py-1 rounded-lg text-[10px] font-bold transition-colors
                                ${isDarkMode ? 'bg-purple-900/30 text-purple-400 hover:bg-purple-900/50' : 'bg-purple-100 text-purple-600 hover:bg-purple-200'}`}
                              title={t('generate_payment')}
                            >
                              + {t('generate_payment')}
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  };

                  const RowLabel = ({ icon, label, color }) => (
                    <span className="text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full" style={{ color, background: `${color}18` }}>
                      {icon} {label}
                    </span>
                  );

                  return (
                    <div className="p-4">
                      {isLoadingFloor ? (
                        <div className="flex justify-center py-10">
                          <div className={`h-8 w-8 animate-spin rounded-full border-4 ${isDarkMode ? 'border-purple-500 border-t-transparent' : 'border-purple-500 border-t-transparent'}`} />
                        </div>
                      ) : apartments.length === 0 ? (
                        <p className={`text-center py-6 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                          {t('no_apartments_on_floor') || 'No apartments found on this floor.'}
                        </p>
                      ) : (
                        <>
                          {/* Month Tabs */}
                          <div className={`flex flex-wrap gap-1.5 mb-4 p-2 rounded-xl ${isDarkMode ? 'bg-zinc-800' : 'bg-zinc-100'}`}>
                            {MONTHS.map((m, i) => {
                              const mo = i + 1;
                              const isCurrent = mo === currentMonth;
                              const isActive = mo === activeMonth;
                              // Count paid/unpaid for this month across all apartments
                              const monthPayments = apartments.map(a => a.payments?.[mo]).filter(Boolean);
                              const paidCount = monthPayments.filter(p => p.isPaid).length;
                              const unpaidCount = monthPayments.filter(p => !p.isPaid).length;
                              return (
                                <button
                                  key={mo}
                                  onClick={() => setZoneSelectedMonth(prev => ({ ...prev, [floor.floor]: mo }))}
                                  className={`relative px-3 py-1.5 rounded-lg text-xs font-bold transition-all
                                    ${isActive
                                      ? (isDarkMode ? 'bg-purple-600 text-white shadow-lg shadow-purple-900/30' : 'bg-purple-500 text-white shadow-md')
                                      : (isDarkMode ? 'bg-zinc-700 text-zinc-400 hover:bg-zinc-600 hover:text-zinc-200' : 'bg-white text-zinc-500 hover:bg-zinc-200 hover:text-zinc-700')
                                    }
                                    ${isCurrent && !isActive ? 'ring-1 ring-purple-500' : ''}`}
                                >
                                  {m}
                                  {/* tiny indicators */}
                                  {paidCount > 0 || unpaidCount > 0 ? (
                                    <span className="absolute -top-1 -right-1 flex gap-px">
                                      {paidCount > 0 && <span className="w-1.5 h-1.5 rounded-full bg-green-500" />}
                                      {unpaidCount > 0 && <span className="w-1.5 h-1.5 rounded-full bg-red-500" />}
                                    </span>
                                  ) : null}
                                </button>
                              );
                            })}
                            <span className={`ml-auto self-center text-xs font-medium ${isDarkMode ? 'text-zinc-400' : 'text-zinc-500'}`}>
                              {MONTHS[activeMonth - 1]} {selectedYear}
                            </span>
                          </div>

                          {/* Map Grid */}
                          <div className="overflow-x-auto pb-2">
                            <div className={`inline-flex gap-4 p-4 rounded-2xl border min-w-max
                              ${isDarkMode ? 'bg-zinc-900/60 border-zinc-700/60' : 'bg-zinc-50/80 border-zinc-200'}`}>

                              {/* Main section: Back row + wall + Front row */}
                              <div className="flex flex-col gap-3">
                                <div className="flex items-center gap-2">
                                  <RowLabel icon="↑" label={t('back_row')} color={isDarkMode ? '#818cf8' : '#6366f1'} />
                                </div>
                                <div className={`flex flex-wrap gap-2 px-3 py-2.5 rounded-xl border border-dashed
                                  ${isDarkMode ? 'border-indigo-700/40 bg-indigo-900/10' : 'border-indigo-200 bg-indigo-50/40'}`}>
                                  {backRow.map(apt => <UnitCard key={apt.apartmentId} apt={apt} />)}
                                </div>

                                {/* Wall */}
                                <div className="flex items-center gap-2 px-1">
                                  <div className={`flex-1 h-2 rounded-full ${isDarkMode ? 'bg-zinc-600' : 'bg-zinc-300'}`} />
                                  <span className={`text-[9px] font-bold uppercase tracking-widest shrink-0 ${isDarkMode ? 'text-zinc-500' : 'text-zinc-400'}`}>─ {t('wall')} ─</span>
                                  <div className={`flex-1 h-2 rounded-full ${isDarkMode ? 'bg-zinc-600' : 'bg-zinc-300'}`} />
                                </div>

                                <div className="flex items-center gap-2">
                                  <RowLabel icon="↓" label={t('front_row')} color={isDarkMode ? '#34d399' : '#059669'} />
                                </div>
                                <div className={`flex flex-wrap gap-2 px-3 py-2.5 rounded-xl border border-dashed
                                  ${isDarkMode ? 'border-emerald-700/40 bg-emerald-900/10' : 'border-emerald-200 bg-emerald-50/40'}`}>
                                  {frontRow.map(apt => <UnitCard key={apt.apartmentId} apt={apt} />)}
                                </div>
                              </div>

                              {/* Corner units — stacked vertically on the right side, matching the floor plan */}
                              {cornerUnits.length > 0 && (
                                <div className="flex flex-col justify-center gap-3 pl-3 border-l-2 border-dashed" style={{ borderColor: isDarkMode ? '#71717a' : '#d4d4d8' }}>
                                  <RowLabel icon="⊞" label="Corner" color={isDarkMode ? '#fb923c' : '#ea580c'} />
                                  <div className={`flex flex-col gap-2 p-2 rounded-xl border border-dashed
                                    ${isDarkMode ? 'border-orange-700/40 bg-orange-900/10' : 'border-orange-200 bg-orange-50/40'}`}>
                                    {cornerUnits.map(apt => <UnitCard key={apt.apartmentId} apt={apt} />)}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  );
                })()}
              </div>
            );
          })}

          {/* Summary Footer */}
          {filteredFloors.length > 0 && (
            <div className={`${isDarkMode ? 'bg-zinc-900 border-gray-700' : 'bg-white border-gray-200'} rounded-xl border shadow-lg p-4`}>
              <div className="flex flex-wrap justify-between items-center">
                <div className={`${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  <span className="font-medium">{t('summary') || 'Summary'}:</span> {t('zone_showroom_count', { zones: filteredFloors.length, showrooms: filteredFloors.reduce((sum, floor) => sum + floor.totalApartments, 0) }) ||
                    `${filteredFloors.length} zones, ${filteredFloors.reduce((sum, floor) => sum + floor.totalApartments, 0)} showroom units`}
                </div>

                <div className={`flex ${isRTL ? 'space-x-4 space-x-reverse' : 'space-x-4'} mt-2 sm:mt-0`}>
                  <div className={`px-3 py-1 rounded-full text-sm ${isDarkMode ? 'bg-green-900 bg-opacity-20 text-green-400' : 'bg-green-100 text-green-800'
                    }`}>
                    {filteredFloors.reduce((sum, floor) => sum + floor.paidCount, 0)} {t('paid') || 'Paid'}
                  </div>
                  <div className={`px-3 py-1 rounded-full text-sm ${isDarkMode ? 'bg-red-900 bg-opacity-20 text-red-400' : 'bg-zinc-100 text-red-800'
                    }`}>
                    {filteredFloors.reduce((sum, floor) => sum + floor.unpaidCount, 0)} {t('unpaid') || 'Unpaid'}
                  </div>
                  <div className={`px-3 py-1 rounded-full text-sm ${isDarkMode ? 'bg-red-900 bg-opacity-20 text-red-400' : 'bg-zinc-100 text-red-800'
                    }`}>
                    {(() => {
                      const totalPayments = filteredFloors.reduce((sum, floor) => sum + floor.totalPayments, 0);
                      const paidPayments = filteredFloors.reduce((sum, floor) => sum + floor.paidCount, 0);
                      return totalPayments > 0 ? Math.round((paidPayments / totalPayments) * 100) : 0;
                    })()}% {t('rate') || 'Rate'}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )
      }

      {/* No Floors Available */}
      {
        selectedBuilding && !loading && floors.length === 0 && (
          <div className={`${isDarkMode ? 'bg-zinc-900 border-gray-700' : 'bg-white border-gray-200'} rounded-xl border shadow-lg p-8 text-center`}>
            <RiHome4Line className={`mx-auto text-6xl mb-4 ${isDarkMode ? 'text-gray-600' : 'text-zinc-400'}`} />
            <h3 className={`text-lg font-semibold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              {t('no_floors_available') || 'No Floors Available'}
            </h3>
            <p className={`mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              {t('no_floors_message') || 'There are no floors with apartments in this building, or no service payments have been generated yet.'}
            </p>
            <Link
              to={`/admin/services/generate`}
              className={`px-4 py-2 rounded-lg inline-flex items-center ${isDarkMode ? 'bg-green-600 text-white hover:bg-green-700' : 'bg-green-500 text-white hover:bg-green-600'
                }`}
            >
              <RiAddLine className={`${isRTL ? 'ml-2' : 'mr-2'}`} size={18} />
              {t('generate_payments') || 'Generate Payments'}
            </Link>
          </div>
        )
      }

    </div >
  );
};

export default ServiceManagement;